import { ExternalLink, FileText } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type { DiscriminatedTreatmentRequestStatusChangedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface TreatmentRequestStatusChangedEventProps {
  event: DiscriminatedTreatmentRequestStatusChangedEvent;
  matterId: string;
}

export const TreatmentRequestStatusChangedEvent: React.FC<
  TreatmentRequestStatusChangedEventProps
> = ({ event, matterId }) => {
  const { payload, occurred_at, user, agent } = event;
  const { treatment_request, old_status, new_status, reason } = payload;

  // Format statuses
  const oldStatusLabel = formatStatus(old_status);
  const newStatusLabel = formatStatus(new_status);

  // Determine badge variant based on new status
  const newStatusVariant = getStatusVariant(new_status);

  // Format reason
  const reasonLabel = reason ? formatReason(reason) : null;

  return (
    <EventContainer>
      <EventHeader
        icon={<FileText size={20} />}
        title="Treatment Request Status Changed"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Status Change */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-600">Status:</span>
            <Badge variant="outline" className="line-through">
              {oldStatusLabel}
            </Badge>
            <span className="text-slate-400">→</span>
            <Badge variant={newStatusVariant}>{newStatusLabel}</Badge>
          </div>

          {/* Reason */}
          {reasonLabel && (
            <div className="text-xs">
              <span className="text-slate-600">Reason:</span>{' '}
              <span className="text-[11px]">{reasonLabel}</span>
            </div>
          )}

          {/* Request Info */}
          {treatment_request && (
            <div className="flex items-center gap-2 text-xs flex-wrap mt-1">
              {treatment_request.is_certified && (
                <Badge variant="default">Certified</Badge>
              )}
              {treatment_request.is_urgent && (
                <Badge variant="destructive">Urgent</Badge>
              )}
              {treatment_request.actor && (
                <Badge variant="outline">
                  {formatActor(treatment_request.actor)}
                </Badge>
              )}
            </div>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/treatment?request_id=${treatment_request.id}&provider_id=${treatment_request.provider_id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Treatment Request
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatStatus(status: string): string {
  const statuses: Record<string, string> = {
    not_ready: 'Not Ready',
    pending: 'Pending',
    request_submitted: 'Request Submitted',
    request_received: 'Request Received',
    awaiting_invoice: 'Awaiting Invoice',
    pending_payment: 'Pending Payment',
    records_pending: 'Records Pending',
    records_delivered: 'Records Delivered',
    records_ingested: 'Records Ingested',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
  };
  return statuses[status] || status;
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'records_delivered':
    case 'records_ingested':
      return 'default'; // Green
    case 'pending':
    case 'request_submitted':
    case 'request_received':
    case 'records_pending':
      return 'secondary'; // Blue
    case 'rejected':
    case 'cancelled':
      return 'destructive'; // Red
    case 'not_ready':
    case 'awaiting_invoice':
    case 'pending_payment':
      return 'outline'; // Gray
    default:
      return 'outline';
  }
}

function formatReason(reason: string): string {
  const reasons: Record<string, string> = {
    treatment_completed: 'Treatment Completed',
    request_submitted_via_portal: 'Submitted via Portal',
    request_submitted_via_mail: 'Submitted via Mail',
    request_submitted_via_fax: 'Submitted via Fax',
    request_submitted_via_email: 'Submitted via Email',
    request_updated_via_voice_ai: 'Updated via Voice AI',
    request_updated_via_portal: 'Updated via Portal',
    invoice_paid_via_portal: 'Invoice Paid via Portal',
    records_ingested_via_portal: 'Records Ingested via Portal',
    manual: 'Manual',
  };
  return reasons[reason] || reason;
}

function formatActor(actor: string): string {
  const actors: Record<string, string> = {
    user: 'User',
    agent: 'Agent',
  };
  return actors[actor] || actor;
}
